import React, { useState, useEffect } from 'react';
import {
  Truck, Plus, Search, Package, ChevronLeft, Loader2,
  Check, Clock, X, Ban, Calendar, User, MapPin,
  FileText, ArrowDown, ArrowUp, Eye, AlertTriangle, Download
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import { useLoader } from '../../contexts/LoaderContext';

const STATUT_CONFIG = {
  planifiee: { label: 'Planifiée', color: '#3b82f6', bg: '#eff6ff', icon: Clock },
  en_cours: { label: 'En transit', color: '#f59e0b', bg: '#fef3c7', icon: Truck },
  livree: { label: 'Livrée', color: '#10b981', bg: '#d4f4dd', icon: Check },
  partielle: { label: 'Partielle', color: '#8b5cf6', bg: '#ede9fe', icon: AlertTriangle },
  annulee: { label: 'Annulée', color: '#9ca3af', bg: '#f3f4f6', icon: Ban },
};

const TYPE_CONFIG = {
  entree: { label: 'Réception', color: '#10b981', bg: '#d4f4dd', icon: ArrowDown },
  sortie: { label: 'Expédition', color: '#3b82f6', bg: '#eff6ff', icon: ArrowUp },
};

const LIGNE_VIDE = { designation: '', quantite_prevue: 1, quantite_livree: 1, unite: 'unité', notes: '' };

const LivraisonsModule = () => {
  const [livraisons, setLivraisons] = useState([]);
  const [totaux, setTotaux] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  const [currentView, setCurrentView] = useState('list');
  const [selectedLivraison, setSelectedLivraison] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Modal création
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [partenaires, setPartenaires] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [formData, setFormData] = useState({
    type_livraison: 'sortie', commande_id: '', partenaire_id: '', partenaire_nom: '',
    date_livraison: new Date().toISOString().split('T')[0], date_prevue: '',
    adresse_livraison: '', transporteur: '', reference_transport: '', notes: '',
  });
  const [lignes, setLignes] = useState([{ ...LIGNE_VIDE }]);

  const { showLoader, hideLoader } = useLoader();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  useEffect(() => { loadLivraisons(); loadPartenaires(); loadCommandes(); }, []);
  useEffect(() => { loadLivraisons(); }, [filterType, filterStatut]);
  useEffect(() => { const t = setTimeout(() => loadLivraisons(), 300); return () => clearTimeout(t); }, [searchTerm]);

  const loadLivraisons = async () => {
    showLoader(); setIsLoading(true);
    try {
      const r = await api.livraisons.getAll({ type: filterType, statut: filterStatut, search: searchTerm });
      if (r.success) { setLivraisons(r.data); setTotaux(r.totaux || {}); }
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); hideLoader(); }
  };

  const loadPartenaires = async () => {
    try { const r = await api.partenaires.getAll(); if (r.success) setPartenaires(r.data); } catch (e) {}
  };
  const loadCommandes = async () => {
    try { const r = await api.commandes.getAll(); if (r.success) setCommandes(r.data.filter(c => c.statut !== 'annulee' && c.statut !== 'facturee')); } catch (e) {}
  };

  const loadDetail = async (liv) => {
    setCurrentView('detail'); setLoadingDetail(true);
    try {
      const r = await api.livraisons.getOne(liv.id);
      if (r.success) setSelectedLivraison(r.data);
    } catch (e) { console.error(e); }
    finally { setLoadingDetail(false); }
  };

  // ============================================================================
  // CRÉATION
  // ============================================================================
  const openCreateModal = () => {
    setFormData({
      type_livraison: 'sortie', commande_id: '', partenaire_id: '', partenaire_nom: '',
      date_livraison: new Date().toISOString().split('T')[0], date_prevue: '',
      adresse_livraison: '', transporteur: '', reference_transport: '', notes: '',
    });
    setLignes([{ ...LIGNE_VIDE }]);
    setShowModal(true);
  };

  const handleSelectCommande = async (commandeId) => {
    if (!commandeId) {
      setFormData(f => ({ ...f, commande_id: '', partenaire_nom: '' }));
      setLignes([{ ...LIGNE_VIDE }]);
      return;
    }
    try {
      const r = await api.livraisons.fromCommande(commandeId);
      if (r.success) {
        const { commande, lignes: lig } = r.data;
        setFormData(f => ({
          ...f, commande_id: commande.id,
          partenaire_id: commande.partenaire_id || '',
          partenaire_nom: commande.partenaire_nom || '',
          type_livraison: commande.type_commande === 'achat' ? 'entree' : 'sortie',
          adresse_livraison: commande.partenaire_adresse || '',
        }));
        setLignes(lig.length > 0 ? lig : [{ ...LIGNE_VIDE }]);
      }
    } catch (e) { console.error(e); }
  };

  const updateLigne = (i, field, value) => { const nl = [...lignes]; nl[i] = { ...nl[i], [field]: value }; setLignes(nl); };
  const addLigne = () => setLignes([...lignes, { ...LIGNE_VIDE }]);
  const removeLigne = (i) => { if (lignes.length > 1) setLignes(lignes.filter((_, j) => j !== i)); };

  const handleCreate = async () => {
    if (lignes.every(l => !l.designation.trim())) { showToast('Au moins une ligne requise', 'error'); return; }
    setIsSaving(true);
    try {
      const r = await api.livraisons.creer({ ...formData, lignes: lignes.filter(l => l.designation.trim()) });
      if (r.success) { showToast(r.message); setShowModal(false); await loadLivraisons(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
    finally { setIsSaving(false); }
  };

  const handleChangerStatut = async (id, statut) => {
    try {
      const r = await api.livraisons.changerStatut(id, statut);
      if (r.success) { showToast(r.message); if (selectedLivraison) loadDetail({ id }); await loadLivraisons(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const inputStyle = {
    onFocus: (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; },
    onBlur: (e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; },
  };

  // ============================================================================
  // VUE LISTE
  // ============================================================================
  const renderList = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Truck size={18} style={{ color: '#448696' }} /><span className="text-xs font-semibold text-gray-500">Total</span></div>
          <div className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{totaux.total || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><ArrowDown size={18} className="text-green-600" /><span className="text-xs font-semibold text-gray-500">Réceptions</span></div>
          <div className="text-2xl font-bold text-green-600">{totaux.nb_entrees || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #3b82f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><ArrowUp size={18} className="text-blue-600" /><span className="text-xs font-semibold text-gray-500">Expéditions</span></div>
          <div className="text-2xl font-bold text-blue-600">{totaux.nb_sorties || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Clock size={18} className="text-amber-600" /><span className="text-xs font-semibold text-gray-500">Planifiées</span></div>
          <div className="text-2xl font-bold text-amber-600">{totaux.nb_planifiees || 0}</div>
        </div>
        <div onClick={openCreateModal} className="bg-white rounded-2xl p-5 cursor-pointer group transition-all hover:shadow-md" style={{ border: '2px dashed #d1d5db' }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#448696'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}>
          <div className="flex items-center justify-center h-full gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}><Plus size={22} style={{ color: '#448696' }} /></div>
            <div><div className="text-sm font-bold text-gray-700 group-hover:text-[#448696]">Nouvelle</div><div className="text-xs text-gray-400">Créer une livraison</div></div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[{ v: '', l: 'Tous' }, { v: 'entree', l: 'Réceptions', c: '#10b981', b: '#d4f4dd' }, { v: 'sortie', l: 'Expéditions', c: '#3b82f6', b: '#eff6ff' }].map(t => (
              <button key={t.v} onClick={() => setFilterType(t.v)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all border-2 ${filterType === t.v ? 'shadow-sm' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                style={filterType === t.v ? { borderColor: t.c || '#448696', backgroundColor: t.b || '#f0f9fb', color: t.c || '#448696' } : {}}>
                {t.l}
              </button>
            ))}
            <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 focus:outline-none">
              <option value="">Tous statuts</option>
              {Object.entries(STATUT_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {isLoading ? null : livraisons.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <Truck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">Aucune livraison</h3>
        </div>
      ) : (
        <div className="space-y-3">
          {livraisons.map((l) => {
            const st = STATUT_CONFIG[l.statut] || STATUT_CONFIG.planifiee;
            const tp = TYPE_CONFIG[l.type_livraison] || TYPE_CONFIG.sortie;
            return (
              <div key={l.id} onClick={() => loadDetail(l)} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-all cursor-pointer" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: tp.bg }}>
                    <tp.icon size={20} style={{ color: tp.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-bold text-gray-900">{l.numero}</span>
                      <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: tp.bg, color: tp.color }}>{tp.label}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400 font-medium">
                      {l.partenaire_nom && <span className="flex items-center gap-1"><User size={11} />{l.partenaire_nom}</span>}
                      <span className="flex items-center gap-1"><Calendar size={11} />{formatDate(l.date_livraison)}</span>
                      {l.commande_numero && <span className="flex items-center gap-1"><FileText size={11} />{l.commande_numero}</span>}
                      {l.transporteur && <span className="flex items-center gap-1"><Truck size={11} />{l.transporteur}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">{l.nb_lignes} article{l.nb_lignes > 1 ? 's' : ''}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // ============================================================================
  // VUE DÉTAIL
  // ============================================================================
  const renderDetail = () => {
    if (loadingDetail) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>;
    if (!selectedLivraison) return null;
    const l = selectedLivraison;
    const st = STATUT_CONFIG[l.statut] || STATUT_CONFIG.planifiee;
    const tp = TYPE_CONFIG[l.type_livraison] || TYPE_CONFIG.sortie;

    return (
      <>
        <button onClick={() => { setCurrentView('list'); setSelectedLivraison(null); }}
          className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-5">
          <ChevronLeft size={18} /> Retour
        </button>

        <div className="bg-white rounded-2xl p-6 mb-6" style={{ borderLeft: `4px solid ${tp.color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{l.numero}</h2>
                <span className="text-xs px-3 py-1 rounded-lg font-semibold" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                <span className="text-xs px-3 py-1 rounded-lg font-semibold" style={{ backgroundColor: tp.bg, color: tp.color }}>{tp.label}</span>
              </div>
              {l.commande_numero && <p className="text-sm text-gray-500">Commande : <b>{l.commande_numero}</b></p>}
            </div>
          </div>

          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100 flex-wrap">
            {l.statut === 'planifiee' && (
              <button onClick={() => handleChangerStatut(l.id, 'en_cours')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center gap-1"><Truck size={14} /> En transit</button>
            )}
            {(l.statut === 'planifiee' || l.statut === 'en_cours') && (
              <>
                <button onClick={() => handleChangerStatut(l.id, 'livree')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-green-50 text-green-700 hover:bg-green-100 flex items-center gap-1"><Check size={14} /> Livrée</button>
                <button onClick={() => handleChangerStatut(l.id, 'partielle')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-purple-50 text-purple-700 hover:bg-purple-100 flex items-center gap-1"><AlertTriangle size={14} /> Partielle</button>
              </>
            )}
            {l.statut !== 'livree' && l.statut !== 'annulee' && (
              <button onClick={() => handleChangerStatut(l.id, 'annulee')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1"><Ban size={14} /> Annuler</button>
            )}
            <button onClick={async () => {
              try {
                showToast('Génération du PDF en cours...', 'loading');
                await api.livraisons.exportPDF(l.id);
                showToast('PDF téléchargé !', 'success');
              } catch (e) { showToast('Erreur génération PDF', 'error'); }
            }} className="px-4 py-2 rounded-xl text-xs font-semibold bg-gray-50 text-gray-700 hover:bg-gray-100 flex items-center gap-1 border border-gray-200">
              <Download size={14} /> Télécharger PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lignes */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="px-6 py-4 border-b border-gray-100"><h3 className="text-sm font-bold text-gray-800">Articles livrés</h3></div>
            <div className="divide-y divide-gray-50">
              {(l.lignes || []).map((lg, i) => {
                const prevue = parseFloat(lg.quantite_prevue || 0);
                const livree = parseFloat(lg.quantite_livree || 0);
                const pct = prevue > 0 ? (livree / prevue * 100) : 0;
                return (
                  <div key={i} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-gray-900">{lg.designation}</span>
                      <span className="text-sm font-bold" style={{ color: pct >= 100 ? '#10b981' : pct > 0 ? '#f59e0b' : '#ef4444' }}>
                        {livree} / {prevue} {lg.unite}
                      </span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: pct >= 100 ? '#10b981' : pct > 0 ? '#f59e0b' : '#ef4444' }} />
                    </div>
                    {lg.notes && <p className="text-xs text-gray-400 mt-1">{lg.notes}</p>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">Détails</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Partenaire</span><span className="font-medium">{l.partenaire_nom || '-'}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Date</span><span className="font-medium">{formatDate(l.date_livraison)}</span></div>
                {l.date_prevue && <div className="flex justify-between text-sm"><span className="text-gray-500">Prévue</span><span className="font-medium">{formatDate(l.date_prevue)}</span></div>}
                {l.transporteur && <div className="flex justify-between text-sm"><span className="text-gray-500">Transporteur</span><span className="font-medium">{l.transporteur}</span></div>}
                {l.reference_transport && <div className="flex justify-between text-sm"><span className="text-gray-500">Réf. transport</span><span className="font-medium">{l.reference_transport}</span></div>}
              </div>
            </div>
            {l.adresse_livraison && (
              <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">Adresse</h4>
                <p className="text-sm text-gray-700">{l.adresse_livraison}</p>
              </div>
            )}
            {l.notes && (
              <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">Notes</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{l.notes}</p>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {currentView === 'list' ? renderList() : renderDetail()}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[92vh] overflow-y-auto">
            <div className="px-8 py-5 rounded-t-2xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              <Plus size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Nouvelle livraison</h3>
            </div>
            <div className="p-8">
              {/* Type */}
              <div className="flex gap-3 mb-6">
                {Object.entries(TYPE_CONFIG).map(([k, v]) => {
                  const sel = formData.type_livraison === k;
                  return (
                    <button key={k} onClick={() => setFormData(f => ({ ...f, type_livraison: k }))}
                      className={`flex-1 p-4 rounded-xl text-center transition-all border-2 ${sel ? 'shadow-sm' : 'border-gray-200 hover:bg-gray-50'}`}
                      style={sel ? { borderColor: v.color, backgroundColor: v.bg } : {}}>
                      <v.icon size={22} className="mx-auto mb-1" style={{ color: sel ? v.color : '#9ca3af' }} />
                      <div className={`text-sm font-bold ${sel ? '' : 'text-gray-600'}`} style={sel ? { color: v.color } : {}}>{v.label}</div>
                    </button>
                  );
                })}
              </div>

              {/* Depuis commande */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Depuis un bon de commande (optionnel)</label>
                <select value={formData.commande_id} onChange={(e) => handleSelectCommande(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle}>
                  <option value="">Livraison libre (sans commande)</option>
                  {commandes.map(c => <option key={c.id} value={c.id}>{c.numero} — {c.partenaire_nom || 'N/A'}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Partenaire</label>
                  <select value={formData.partenaire_id} onChange={(e) => {
                    const p = partenaires.find(x => x.id === parseInt(e.target.value));
                    setFormData(f => ({ ...f, partenaire_id: e.target.value, partenaire_nom: p ? p.nom : '' }));
                  }} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle}>
                    <option value="">Sélectionner...</option>
                    {partenaires.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                </div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Transporteur</label>
                  <input type="text" value={formData.transporteur} onChange={(e) => setFormData(f => ({ ...f, transporteur: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Date livraison</label>
                  <input type="date" value={formData.date_livraison} onChange={(e) => setFormData(f => ({ ...f, date_livraison: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Réf. transport</label>
                  <input type="text" value={formData.reference_transport} onChange={(e) => setFormData(f => ({ ...f, reference_transport: e.target.value }))} placeholder="N° suivi..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
              </div>

              {/* Lignes */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-bold text-gray-700">Articles</label>
                  <button onClick={addLigne} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#448696' }}>+ Ligne</button>
                </div>
                <div className="space-y-2">
                  {lignes.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded-xl p-3">
                      <div className="col-span-5">
                        <input type="text" placeholder="Désignation *" value={l.designation} onChange={(e) => updateLigne(i, 'designation', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none" {...inputStyle} />
                      </div>
                      <div className="col-span-2">
                        <input type="number" placeholder="Prévue" value={l.quantite_prevue} onChange={(e) => updateLigne(i, 'quantite_prevue', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-center focus:outline-none" />
                      </div>
                      <div className="col-span-2">
                        <input type="number" placeholder="Livrée" value={l.quantite_livree} onChange={(e) => updateLigne(i, 'quantite_livree', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-center focus:outline-none" />
                      </div>
                      <div className="col-span-2">
                        <input type="text" placeholder="Unité" value={l.unite} onChange={(e) => updateLigne(i, 'unite', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none" />
                      </div>
                      <div className="col-span-1 text-center">
                        <button onClick={() => removeLigne(i)} className="w-7 h-7 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-50 hover:text-red-600"><X size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-6"><label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))} rows="2"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>

              <div className="flex gap-3">
                <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
                <button onClick={handleCreate} disabled={isSaving} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50" style={{ backgroundColor: '#448696' }}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Créer la livraison'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

export default LivraisonsModule;